import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spark } from './icons';
import { useUnsavedApi } from './UnsavedChanges';

interface VersionFile {
  version: string;
  build: string;
  notes?: string[];
}

export interface VersionGateProps {
  /** Build id baked into this bundle (e.g. git sha). */
  currentBuild: string;
  url?: string;
  intervalMs?: number;
}

/**
 * Mandatory update (PLAN §10, decided): polls /version.json every 5 min and on tab focus. When the build changes,
 * a full-screen blocking overlay appears with a single "Refresh now" button ("Save & refresh" if there are
 * unsaved changes). No "Later"; the rest of the page becomes inert.
 */
export function VersionGate({ currentBuild, url = '/version.json', intervalMs = 5 * 60_000 }: VersionGateProps) {
  const [next, setNext] = useState<VersionFile | null>(null);
  const [busy, setBusy] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const unsaved = useUnsavedApi();

  const check = useCallback(async () => {
    try {
      const r = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const j = (await r.json()) as VersionFile;
      if (j.build && j.build !== currentBuild) setNext(j);
    } catch {
      /* offline: try again later */
    }
  }, [url, currentBuild]);

  useEffect(() => {
    if (currentBuild === 'dev') return;
    const id = window.setInterval(check, intervalMs);
    const onVis = () => document.visibilityState === 'visible' && void check();
    document.addEventListener('visibilitychange', onVis);
    // A stale tab after a deploy fails to load lazy chunks; treat that as "new version available".
    const onChunkError = () => void check();
    window.addEventListener('vite:preloadError', onChunkError);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('vite:preloadError', onChunkError);
    };
  }, [check, intervalMs, currentBuild]);

  useEffect(() => {
    if (!next) return;
    const others = [...document.body.children].filter((c) => !c.classList.contains('ui-version-block'));
    others.forEach((c) => c.setAttribute('inert', ''));
    document.documentElement.style.overflow = 'hidden';
    btn.current?.focus();
    const trap = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); btn.current?.focus(); }
    };
    document.addEventListener('keydown', trap, true);
    return () => {
      others.forEach((c) => c.removeAttribute('inert'));
      document.removeEventListener('keydown', trap, true);
    };
  }, [next]);

  if (!next) return null;
  const dirty = unsaved.isDirty();
  const refresh = async () => {
    setBusy(true);
    if (unsaved.isDirty()) await unsaved.saveAll();
    window.__missionsReloading = true;
    window.location.reload();
  };
  return createPortal(
    <div className="ui-version-block in" role="alertdialog" aria-modal="true" aria-labelledby="ui-ver-t" aria-describedby="ui-ver-d">
      <div className="ui-version-card">
        <div className="badge"><Spark /></div>
        <small className="ver">Update {next.version}</small>
        <h3 id="ui-ver-t">A new version is ready</h3>
        <p id="ui-ver-d">
          {dirty ? 'Your changes will be saved first. Then the app refreshes to the latest version.' : "Refresh to continue. This takes a few seconds and you'll stay signed in."}
        </p>
        {next.notes && next.notes.length > 0 && (
          <div className="notes"><b>What's new</b><ul>{next.notes.map((n) => <li key={n}>{n}</li>)}</ul></div>
        )}
        <button ref={btn} type="button" className={`ui-btn primary${busy ? ' is-loading' : ''}`} onClick={() => void refresh()}>
          {busy ? (dirty ? 'Saving…' : 'Refreshing…') : dirty ? 'Save & refresh' : 'Refresh now'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
